import { Section2Form } from '@/features/section-2/application/validators/section-2.schema';
import { Section2Response, Section2ResponseDto } from '../dto/res/Section2ResponseDto';
import { Section2UpsertRequestDto } from '../dto/req/Section2RequestDto';
import Section2 from '@/features/section-2/application/domain/entities/Section2';
import { Section2Steps } from '@/features/section-2/application/domain/models/section-2-steps';
import SectionOneResponseDto from '@/features/section-1/infra/repositories/dto/res/SectionOneResponseDto';
import { Section1Summary } from '@/features/section-2/application/domain/entities/Section2';
import { formatDate } from '@/shared/infra/utils/utils';
import { statusToSave } from '@/features/section-2/application/domain/entities/DocumentOptions';

/** The visa status choice as the document step's form fields; only a selection is shown as one. */
export function workAuthorizationFormFields(
  wa: Section2Response['workAuthorization'] | undefined
): Partial<Section2Form> {
  if (!wa) return {};
  return {
    workAuthorizationStatusCode: wa.determinedBy === 'selected' ? wa.statusCode : '',
    listADocumentTypeCode: wa.documentTypeCodes.A ?? '',
    listBDocumentTypeCode: wa.documentTypeCodes.B ?? '',
    listCDocumentTypeCode: wa.documentTypeCodes.C ?? '',
    offMatrixReason: wa.offMatrixReason ?? '',
  };
}

export function mapSection2QueryResposeDtoToEntity(dto: Section2Response): Section2 {
  const core = dto.coreFields;
  return {
    formFields: {
      // Step 1
      firstWorkDay: core.firstDayOfEmployment
        ? formatDate(new Date(core.firstDayOfEmployment))
        : undefined,
      delayReasonType: core.reasonOfDelay ?? undefined,
      delayReasonOther: core.otherReasonForDelay ?? '',

      // Step 2
      listASelection: core.listADocKey ?? '',
      listBSelection: core.listBDocKey ?? '',
      listCSelection: core.listCDocKey ?? '',
      ...workAuthorizationFormFields(dto.workAuthorization),

      // Step 3 - Convert ISO dates in metadata to MM/dd/yyyy
      documentMetadata: {
        listA: core.listADocMetadata ?? {},
        listB: core.listBDocMetadata ?? {},
        listC: core.listCDocMetadata ?? {},
      },

      // Step 4 (map new API fields to form fields)
      isLoggedInUserSigner: core.isAuthorisedUser,
      signerFullName: core.isAuthorisedUser ? '' : (core.signerName ?? ''),
      signatureText: core.signature ?? '',
      signatureTimestamp: core.signatureDate ? new Date(core.signatureDate) : undefined,
      confirmationChecked: core.isStatementRead as true | undefined,
      // Null is never filled in, which the signature step prefills; '' was cleared on purpose.
      additionalInformation: core.additionalInformation ?? undefined,
      alternativeProcedureUsed: core.alternativeProcedureUsed ?? null,
    },
    i9FormId: dto.i9FormId,
    currentStep: core.currentStep
      ? mapStepNumberToStep(core.currentStep)
      : Section2Steps.EmploymentStart,
    savedAt: core.savedAt,
    status: dto.status,
    eVerifyCaseQueueId: null,
    businessAddress: dto.businessAddress,
    businessName: dto.businessName,
    signerName: core.signerName ?? '',
    signerTitle: dto.signerTitle ?? null,
    isRemoteI9: dto.remoteI9Flag,
    remoteI9Status: dto.remoteI9Status,
    remoteI9Type: dto.remoteI9Type,
    hireDateFromEmployer: dto.hireDateFromEmployer,
    suggestedAdditionalInformation: dto.suggestedAdditionalInformation ?? [],
    printedListARows: dto.printedListARows ?? null,
    printedListBRows: dto.printedListBRows ?? null,
    printedListCRows: dto.printedListCRows ?? null,
    additionalInformationLimits: dto.additionalInformationLimits,
    additionalInformationContinued: dto.additionalInformationContinued ?? [],
    alternativeProcedureUsed: core.alternativeProcedureUsed ?? null,
    awaitingInPersonReview: dto.awaitingInPersonReview === true,
    section1Correction: dto.section1Correction
      ? {
          correctedAt: new Date(dto.section1Correction.correctedAt),
          reason: dto.section1Correction.reason ?? null,
        }
      : null,
  };
}
/**
 * Maps Section 2 Response DTO to Domain Entity
 * Returns formFields that can be directly used to reset form data
 */
export function mapSection2MutationResponseDtoToEntity(dto: Section2ResponseDto): Section2 {
  const core = dto.section2.coreFields;
  return {
    formFields: {
      // Step 1
      firstWorkDay: core.firstDayOfEmployment
        ? formatDate(new Date(core.firstDayOfEmployment))
        : undefined,
      delayReasonType: core.reasonOfDelay ?? undefined,
      delayReasonOther: core.otherReasonForDelay ?? '',

      // Step 2
      listASelection: core.listADocKey ?? '',
      listBSelection: core.listBDocKey ?? '',
      listCSelection: core.listCDocKey ?? '',
      ...workAuthorizationFormFields(dto.section2.workAuthorization),

      // Step 3 - Convert ISO dates in metadata to MM/dd/yyyy
      documentMetadata: {
        listA: core.listADocMetadata ?? {},
        listB: core.listBDocMetadata ?? {},
        listC: core.listCDocMetadata ?? {},
      },

      // Step 4 (map new API fields to form fields)
      isLoggedInUserSigner: core.isAuthorisedUser,
      signerFullName: core.isAuthorisedUser ? (core.signerName ?? '') : '',
      signatureText: core.signature ?? '',
      signatureTimestamp: core.signatureDate ? new Date(core.signatureDate) : undefined,
      confirmationChecked: core.isStatementRead as true | undefined,
      // Null is never filled in, which the signature step prefills; '' was cleared on purpose.
      additionalInformation: core.additionalInformation ?? undefined,
      alternativeProcedureUsed: core.alternativeProcedureUsed ?? null,
    },
    i9FormId: dto.section2.i9FormId,
    currentStep: core.currentStep
      ? mapStepNumberToStep(core.currentStep)
      : Section2Steps.EmploymentStart,
    savedAt: core.savedAt,
    status: dto.section2.status,
    eVerifyCaseQueueId: dto.eVerifyCaseQueueId,
    businessAddress: dto.section2.businessAddress,
    businessName: dto.section2.businessName,
    signerName: core.signerName ?? '',
    signerTitle: dto.section2.signerTitle ?? null,
    isRemoteI9: dto.section2.remoteI9Flag,
    remoteI9Status: dto.section2.remoteI9Status,
    remoteI9Type: dto.section2.remoteI9Type,
    hireDateFromEmployer: dto.section2.hireDateFromEmployer,
    suggestedAdditionalInformation: dto.section2.suggestedAdditionalInformation ?? [],
    printedListARows: dto.section2.printedListARows ?? null,
    printedListBRows: dto.section2.printedListBRows ?? null,
    printedListCRows: dto.section2.printedListCRows ?? null,
    additionalInformationLimits: dto.section2.additionalInformationLimits,
    additionalInformationContinued: dto.section2.additionalInformationContinued ?? [],
    alternativeProcedureUsed: core.alternativeProcedureUsed ?? null,
  };
}

/**
 * Maps Section 2 Form values to Request DTO
 * Document metadata values are now always strings (URLs or other values)
 * Files are uploaded immediately to blob storage, so only URLs are in the form
 */
export function mapSection2FormToDto(
  values: Section2Form,
  currentStep: Section2Steps,
  i9FormId: string
): Section2UpsertRequestDto {
  /**
   * Only return fields relevant to the current step to simplify backend processing
   * Backend can ignore irrelevant fields based on currentStep value
   * This also prevents accidental overwriting of data from other steps
   */
  switch (currentStep) {
    case Section2Steps.EmploymentStart:
      return {
        currentStep,
        i9FormId,
        firstDayOfEmployment: values.firstWorkDay || undefined,
        reasonOfDelay: values.delayReasonType,
        otherReasonForDelay: values.delayReasonOther,
      };
    case Section2Steps.DocumentSelection:
      return {
        currentStep,
        i9FormId,
        listADocKey: values.listASelection,
        listBDocKey: values.listBSelection,
        listCDocKey: values.listCSelection,
        // The step's whole state: an empty value clears what the server stored before.
        workAuthorizationStatusCode: statusToSave(values.workAuthorizationStatusCode),
        listADocumentTypeCode: values.listADocumentTypeCode || '',
        listBDocumentTypeCode: values.listBDocumentTypeCode || '',
        listCDocumentTypeCode: values.listCDocumentTypeCode || '',
        // Undecided (the examples not loaded) keeps the note rather than clearing it.
        offMatrixReason: values.offMatrix === false ? '' : values.offMatrixReason?.trim() || '',
      };
    case Section2Steps.DocumentMetadata:
      return {
        currentStep,
        i9FormId,
        listADocMetadata: values.documentMetadata?.listA as
          Record<string, string | boolean | null> | undefined,
        listBDocMetadata: values.documentMetadata?.listB as
          Record<string, string | boolean | null> | undefined,
        listCDocMetadata: values.documentMetadata?.listC as
          Record<string, string | boolean | null> | undefined,
      };
    case Section2Steps.AttestationSignature:
      return {
        currentStep,
        i9FormId,
        isAuthorisedUser: values.isLoggedInUserSigner,
        signerName: values.isLoggedInUserSigner ? '' : values.signerFullName,
        signature: values.signatureText,
        signatureDate:
          values.signatureTimestamp instanceof Date
            ? values.signatureTimestamp.toISOString()
            : undefined,
        isStatementRead: values.confirmationChecked,
        // An empty box is sent as cleared; a box never filled in leaves the stored one alone.
        additionalInformation:
          values.additionalInformation == null ? undefined : values.additionalInformation.trim(),
        // Only an answer is sent: where the question is not asked the server keeps its null.
        alternativeProcedureUsed:
          typeof values.alternativeProcedureUsed === 'boolean'
            ? values.alternativeProcedureUsed
            : undefined,
      };
    default:
      return {
        currentStep,
        i9FormId,
      };
  }
}

/**
 * Maps step number to Section2Steps enum
 */
export function mapStepNumberToStep(stepNumber: number): Section2Steps {
  // Section2Steps enum values are: 0, 1, 2, 3, 4
  if (stepNumber >= 0 && stepNumber <= 4) {
    return stepNumber as Section2Steps;
  }
  return Section2Steps.EmploymentStart;
}

/**
 * Maps Section2Steps enum to step number
 */
export function mapStepToStepNumber(step: Section2Steps): number {
  return step;
}

/**
 * Maps Section 1 Response DTO to Section 1 Summary Entity
 */
export function mapSectionOneDtoToSummary(dto: SectionOneResponseDto): Section1Summary {
  return {
    i9FormId: dto.i9FormId,
    firstName: dto.firstName,
    lastName: dto.lastName,
    middleName: dto.middleName,
    otherName: dto.otherName,
    // Not `new Date(null)`, which is the epoch and would read as a real 01/01/1970 birth date.
    dateOfBirth: dto.dateOfBirth ? new Date(dto.dateOfBirth) : null,
    ssn: dto.ssn || '',
    email: dto.email,
    phoneNumber: dto.phoneNumber,
    address: dto.address,
    city: dto.city,
    state: dto.stateId,
    zipCode: dto.zipCode,
    citizenshipStatus: dto.citizenshipStatus!,
    apartmentNumber: dto.appartmentNumber,
  };
}
